import type { ComponentType } from 'react';
import type { RouteObject } from 'react-router';
import { PermissionGate } from '@saas/ui';
import { CALENDAR_READ, CALENDAR_WRITE } from './nav.js';

/** apps/web が集約する。ルート単位で遅延ロード（03 第 I 部 §2） */
type PageModule = { default: ComponentType };

function guarded(load: () => Promise<PageModule>, anyOf: string[]): RouteObject['lazy'] {
  return async () => {
    const m = await load();
    const Page = m.default;
    const Guarded = () => (
      <PermissionGate anyOf={anyOf}>
        <Page />
      </PermissionGate>
    );
    return { Component: Guarded };
  };
}

/** ログイン後（シェルの中・ModuleGate の下） */
export const calendarRoutes: RouteObject[] = [
  { path: '/calendar', lazy: guarded(() => import('./pages/CalendarMonthPage.js'), CALENDAR_READ) },
  { path: '/calendar/day', lazy: guarded(() => import('./pages/CalendarDayPage.js'), CALENDAR_READ) },
  { path: '/calendar/events/new', lazy: guarded(() => import('./pages/EventFormPage.js'), CALENDAR_WRITE) },
  { path: '/calendar/events/:id', lazy: guarded(() => import('./pages/EventDetailPage.js'), CALENDAR_READ) },
  { path: '/calendar/events/:id/edit', lazy: guarded(() => import('./pages/EventFormPage.js'), CALENDAR_WRITE) },
  // 自分のカレンダーを繋ぐ（OAuth・取り込みと、選べば書き出し）。管理者でなくても自分の分は繋げる
  { path: '/calendar/connections', lazy: guarded(() => import('./pages/ConnectionsPage.js'), CALENDAR_READ) },
  // 自分の就業時間（人の空きの表示・日程調整リンクの候補に効く）。本人の設定なので予定を読めれば誰でも
  { path: '/calendar/working-hours', lazy: guarded(() => import('./pages/WorkingHoursPage.js'), [...CALENDAR_READ, ...CALENDAR_WRITE]) },
  { path: '/calendar/oauth/:provider', lazy: guarded(() => import('./pages/OAuthCallbackPage.js'), CALENDAR_READ) },
  { path: '/calendar/rooms', lazy: guarded(() => import('./pages/RoomsPage.js'), ['calendar.room.manage']) },
  { path: '/calendar/external-sources', lazy: guarded(() => import('./pages/ExternalSourcesPage.js'), ['calendar.external.manage']) },
];

/**
 * ログイン不要の日程調整の公開ページ（SFA §15.5）。URL は ${APP_URL}/b/<token>（発行の応答の url）。
 * `apps/web/src/app/router.tsx` で crm の公開フォーム（`crmPublicRoutes`）・storage の共有リンクと同じ場所 —
 * PublicOnly / RequireAuth / Shell / ModuleGate の外 — に並べる（社外の相手が開く）
 */
export const calendarPublicRoutes: RouteObject[] = [
  {
    path: '/b/:token',
    lazy: async () => {
      const m: PageModule = await import('./pages/PublicBookingPage.js');
      return { Component: m.default };
    },
  },
];
